import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  HostBinding,
  inject,
  Input,
  signal,
  TemplateRef,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  NgForm,
  ReactiveFormsModule
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Observable, take } from 'rxjs';

import { LabelOption } from '@treetop/shared/model';

import { AutocompleteOptionDirective } from '../autocomplete/autocomplete-option.directive';
import { AutocompleteOriginDirective } from '../autocomplete/autocomplete-origin.directive';
import { AutocompleteTriggerDirective } from '../autocomplete/autocomplete-trigger.directive';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { FormFieldControl } from '../form-field/form-field-control';
import { ErrorStateMatcher } from '../forms/error-options';
import { ErrorStateTracker } from '../forms/error-state-tracker';
import { InputDirective } from '../input';
import { TagGridService } from './tag-grid.service';
import { IconDirective } from '../icon/icon.directive';

/**
 * A component that allows the user to select multiple tags from a list of possible options.
 * The selected tags are displayed as chips.
 * The available options need to be provided as an input. The value must be an array of objects with a `value` and a `label` property.
 * The value property must have a property `id` that is unique for each option.
 */
@Component({
  selector: 'tt-tag-grid',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputDirective,
    AutocompleteOptionDirective,
    AutocompleteOriginDirective,
    AutocompleteComponent,
    AutocompleteTriggerDirective,
    MatIconModule,
    IconDirective,
    NgTemplateOutlet,
    CdkDropList,
    CdkDrag,
  ],
  templateUrl: './tag-grid.component.html',
  styleUrls: ['tag-grid.component.scss'],
  providers: [TagGridService, { provide: FormFieldControl, useExisting: TagGridComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagGridComponent<T extends { id: string }> implements FormFieldControl, DoCheck, ControlValueAccessor {
  private service = inject(TagGridService);
  public ngControl = inject(NgControl, { optional: true, self: true });
  private defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private parentForm = inject(NgForm, { optional: true });
  private parentFormGroup = inject(FormGroupDirective, { optional: true });

  @HostBinding('class.tag-grid--disabled') public get isDisabled() {
    return this.ngControl?.disabled;
  }

  public _error = signal(false);
  @Input()
  public set error(value: boolean) {
    this._error.set(value);
  }
  @Input() protected testId: string;
  @Input() public optionTemplate!: TemplateRef<any>;
  @Input() public selectedTemplate!: TemplateRef<any>;

  @Input()
  public set possibleOptions(options: LabelOption<T>[]) {
    this.service.setPossibleLabelOptions(options);
  }
  protected _possibleOptions = this.service.possibleLabelOptions;

  protected _placeholder = signal('');
  @Input()
  public set placeholder(value: string) {
    this._placeholder.set(value);
  }

  protected _disabled = signal(false);
  @Input() public set disabled(disabled: boolean) {
    this._disabled.set(disabled);
  }

  /**
   * Callback function that is called before a tag is removed.
   * If the function returns an observable that emits `true`, the tag will be removed.
   */
  @Input() public beforeRemoveCallback: (tag: T | string) => Observable<boolean>;

  protected _selectedLabelOptions = this.service.selectedLabelOptions;
  public errorState = computed(() => this.errorStateTracker.errorState());
  private errorStateTracker: ErrorStateTracker;

  protected searchControl = new FormControl('');
  private searchControlValue = toSignal(this.searchControl.valueChanges);
  protected inputFocused = signal(false);

  protected filteredPossibleOptions = computed(() => {
    const searchControlValue = this.searchControlValue();
    const possibleOptions = this._possibleOptions();
    const isFocused = this.inputFocused();

    if (isFocused && !searchControlValue) {
      return possibleOptions;
    }

    if (!searchControlValue) {
      return [];
    }

    return possibleOptions.filter((option) => option.label.toLowerCase().includes(searchControlValue.toLowerCase()));
  });

  constructor() {
    effect(() => {
      if (this._disabled()) {
        untracked(() => this.searchControl.disable());
      }
    });

    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }

    this.errorStateTracker = new ErrorStateTracker(this.defaultErrorStateMatcher, this.ngControl, this.parentFormGroup, this.parentForm);

    effect(() => {
      if (this.ngControl) {
        const errorState = this.errorStateTracker.errorState();
        untracked(() => this._error.set(errorState));
      }

      if (!this.inputFocused()) {
        this._onTouched();
      }
    });
  }

  protected onInputFocus(): void {
    this.inputFocused.set(true);
  }

  protected handleOptionSelect(option: string): void {
    this.service.addSelection(option);
    this.searchControl.setValue('');
    this._onChange(this.service.selectedOptions());
  }

  protected removeTag(tag: T, event?: MouseEvent): void {
    event?.preventDefault();

    if (!this.beforeRemoveCallback) {
      this.service.removeSelection(tag);
      this._onChange(this.service.selectedOptions());
      return;
    }

    this.beforeRemoveCallback(tag)
      .pipe(take(1))
      .subscribe((canRemove) => {
        if (canRemove) {
          this.service.removeSelection(tag);
          this._onChange(this.service.selectedOptions());
        }
      });
  }

  // ------------- ControlValueAccessor related methods -------------

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onChange: (value: unknown) => void = () => {};

  public ngDoCheck() {
    if (this.ngControl) {
      this.errorStateTracker.updateErrorState();
    }
  }

  public writeValue(value: T[] | string[]): void {
    this.service.setSelectedOptions(value ?? []);
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  protected handleKeyDown($event: KeyboardEvent) {
    if ($event.key === 'Backspace' && this.searchControl.value === '') {
      $event.preventDefault();

      if (this._selectedLabelOptions().length !== 0) {
        const lastTag = this._selectedLabelOptions()[this._selectedLabelOptions().length - 1];
        this.removeTag(lastTag.value);
      }
    }
  }

  public setDisabledState(isDisabled: boolean) {
    this._disabled.set(isDisabled);
  }

  protected handleElementDropped($event: CdkDragDrop<T, any>) {
    if ($event.previousIndex === $event.currentIndex) {
      return;
    }

    this.service.reorderSelections($event.previousIndex, $event.currentIndex);
    this._onChange(this.service.selectedOptions());
  }
}
